/**
 * BallEntity - Pushable ball with momentum-based continuous movement
 *
 * Balls can be pushed by the player or other entities. Once pushed,
 * they continue moving in that direction until hitting an obstacle.
 */

import { BaseTile, MoveContext, MoveResult, TextureDescriptor } from "./BaseTile";
import { TILE, isEmpty, isArrowTile, getArrowDirection } from "../constants/gameConstants";
import { TileIndex } from "../types";
import { Direction } from "../utils/Direction";
import { GAME_CONFIG } from "../config";
import { logger } from "../utils/logger";

export class BallEntity extends BaseTile {
  // State
  isMoving: boolean = false; // Whether ball has momentum
  moveDirection: Direction | null = null; // Current direction of movement
  //pendin
  // Movement timing
  timeSinceLastMove: number = 0;
  moveInterval: number = 200; // Move every 200ms when in motion

  // Forced arrow movement state
  private forcedDirection?: Direction;

  constructor({
    row,
    col,
    id,
  }: {
    row: number;
    col: number;
    id?: string;
  }) {
    super(id ?? `ball-${col}-${row}-${Date.now()}`, col, row);
  }

  tileType(): TileIndex {
    return TILE.BALL;
  }

  textureDescriptor(): TextureDescriptor {
    return { type: "png", path: "tiles/ball.png" };
  }

  isEntity(): boolean {
    return true; // Ball is a movable entity
  }

  /**
 * Queue a move direction
 */
  queueMove(direction: Direction): void {
    this.moveDirection = direction;
  }

  /**
   * Clear any pending move
   */
  clearPendingMove(): void {
    this.moveDirection = null;
  }

  /**
   * Set forced movement direction (when entering arrow tile)
   */
  private setForcedDirection(direction: Direction): void {
    this.forcedDirection = direction;
    this.timeSinceLastMove = 0;
  }

  /**
   * Clear forced movement direction (when exiting arrow tile)
   */
  clearForcedDirection(): void {
    this.forcedDirection = undefined;
    //this.timeSinceLastMove = 0;
  }

  move(context: MoveContext): MoveResult {
    // Check if we're on an arrow tile
    const currentTile = context.getAt(this.coordinates()).tile;
    if (currentTile && isArrowTile(currentTile)) {
      const arrowDirection = getArrowDirection(currentTile);
      if (this.forcedDirection?.value !== arrowDirection.value)
        this.setForcedDirection(arrowDirection);
      // If not already moving, start moving in arrow direction
      this.isMoving = true;
      this.moveDirection = arrowDirection;
      logger.debug(`ball id ${this.id} being forced to move to ${arrowDirection.value}`);
    } else {
      // Not on arrow tile - clear forced direction
      this.clearForcedDirection();
    }

    // If not moving, wait for external push
    if (!this.isMoving || !this.moveDirection) {
      return { type: "wait" };
    }

    // Accumulate time
    this.timeSinceLastMove += context.deltaMs;

    // Use arrowTileSpeed when forced, moveInterval otherwise
    const speed = this.forcedDirection ? GAME_CONFIG.arrowTileSpeed : this.moveInterval;

    // Not ready to move yet?
    if (this.timeSinceLastMove < speed) {
      console.log("Ball waiting:", this.timeSinceLastMove, "<", speed);
      return { type: "wait" };
    }

    const oldTime = this.timeSinceLastMove;
    this.timeSinceLastMove = 0;

    // Try to continue moving in current direction
    const target = context.getAtDirection(this.coordinates(), this.moveDirection);
    const currentDirection = this.moveDirection; // Save before potentially nulling
    //console.log("Ball trying to move:", this.moveDirection, "to", target);
    // Check for enemy collisions (moving ball kills both)
    if (
      target.entity === TILE.BEE_HORIZONTAL ||
      target.entity === TILE.BEE_VERTICAL ||
      target.entity === TILE.BUG ||
      target.entity === TILE.CAT
    ) {
      // Moving ball collides with enemy - both die
      this.isMoving = false;
      this.moveDirection = null;
      return { type: "die", collisionWith: "entity", who: "both", direction: currentDirection };
    }

    if (this.forcedDirection && (target.entity === TILE.WATER_BLOCK || target.entity === TILE.BALL)) {
      return { type: "push", direction: this.moveDirection, withMomentum: true };
    }

    // Check for bomb collision (both explode)
    if (target.tile === TILE.BOMB) {
      this.isMoving = false;
      this.moveDirection = null;
      return { type: "die", collisionWith: "tile", who: "both", direction: currentDirection };
    }

    if (target.tile === TILE.BUTTON) {
      return { type: "pressButton", direction: this.moveDirection };
    }

    if (target.tile === TILE.ROTATION_BUTTON) {
      return { type: "triggerRotation", direction: this.moveDirection };
    }


    if (target.entity === TILE.BALL) {
      return { type: "push", direction: this.moveDirection };
    }

    if (this.canMoveTo(target)) {
      return { type: "move", direction: this.moveDirection };
    } else {
      // Hit obstacle
      this.timeSinceLastMove = oldTime; // Restore time for next attempt
      // If forced by arrow, stay moving and keep trying
      if (this.forcedDirection) {
        return { type: "wall" };
      } else {
        // Normal momentum - stop moving
        this.isMoving = false;
        this.moveDirection = null;
        return { type: "wall" };
      }
    }
  }

  /**
   * Check if ball can move to target position
   */
  private canMoveTo(target: { tile: TileIndex | null; entity: TileIndex | null }): boolean {
    const { tile, entity } = target;

    // Out of bounds
    if (tile === null) {
      return false;
    }
    const actAsWalls: TileIndex[] = [
      TILE.WALL,
      TILE.DOOR_GREEN,
      TILE.DOOR_RED,
      TILE.DOOR_ORANGE,
      TILE.DOOR_BLUE,
      TILE.BUTTON_DOOR,
      TILE.EXIT,
      TILE.WATER_BLOCK,
      TILE.SAND
    ]


    if (actAsWalls.includes(tile)) {
      return false;
    }

    // Check entity layer - any entity blocks
    if (!isEmpty(entity)) {
      return false;
    }

    // Otherwise passable
    return true;
  }

  /**
   * Start the ball moving in a direction (called when pushed)
   */
  startMoving(direction: Direction): void {
    this.isMoving = true;
    this.moveDirection = direction;
    this.timeSinceLastMove = 0;
  }

  /**
   * Stop the ball's momentum
   */
  stopMoving(): void {
    this.isMoving = false;
    this.moveDirection = null;
  }

  /**
   * Check if ball can be pushed to a target position
   */
  canBePushed(
    targetTile: TileIndex | null,
    targetEntity: TileIndex | null
  ): boolean {
    return this.canMoveTo({ tile: targetTile, entity: targetEntity });
  }

  clone(): BallEntity {
    const copy = new BallEntity({
      col: this.col,
      row: this.row,
      id: this.id,
    });
    copy.isMoving = this.isMoving;
    copy.moveDirection = this.moveDirection;
    copy.timeSinceLastMove = this.timeSinceLastMove;
    copy.forcedDirection = this.forcedDirection;
    copy.sprite = this.sprite;
    return copy;
  }

  toJSON(): Record<string, any> {
    return {
      type: "ball",
      id: this.id,
      col: this.col,
      row: this.row,
      isMoving: this.isMoving,
      moveDirection: this.moveDirection?.value,
      timeSinceLastMove: this.timeSinceLastMove,
      forcedDirection: this.forcedDirection,
    };
  }
}
